import {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import Dimmer from "@/components/UI/Dimmer/Dimmer";
import {onAuthStateChanged} from "@firebase/auth";
import {auth, firestore} from "@/services/firebase.service";
import {doc, onSnapshot, setDoc} from "@firebase/firestore";
import {gameStatEmergencyError} from "@/utils/gameStatEmergencyChecker";
import {ToastService} from "@/components/ToastProvider/ToastProvider";


type Setter<T> = (patch: Partial<T>) => Promise<void> | void;
type FLSContextType<T> = { state: T; setState: Setter<T>; loading: boolean };

const FLSContext = createContext<FLSContextType<any> | null>(null);
type Props<T extends Record<string, any>> = {
  database: 'ch-games'
  collection: 'userDataSpellify' | 'userDataCrossword'
  defaultState: T,
  children: React.ReactNode
}

const serialize = (obj: Record<string, any>) => {
  const out: Record<string, any> = {}
  for (const key in obj) {
    out[key] = JSON.stringify(obj[key])
  }
  return out
}
const deserialize = (obj: Record<string, any>) => {
  const out: Record<string, any> = {}
  for (const key in obj) {
    try {
      out[key] = JSON.parse(obj[key])
    } catch {
      out[key] = obj[key]
    }
  }
  return out
}

const FLSProvider = <T extends Record<string, any>>({ database, collection, defaultState, children }: Props<T>) => {
  const [localState, setLocalState] = useState<T>(defaultState);
  const [remoteState, setRemoteState] = useState<T | null>(null);
  const [loadingLS, setLoadingLS] = useState(true);
  const [loadingFS, setLoadingFS] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);
  const unsubRef = useRef<(() => void) | null>(null);
  const prevFirestoreDataRef = useRef<T | null>(null);
  const localStateRef = useRef(localState);

  // Handle localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return
    const storageData: Partial<T> = {...defaultState}
    for (const key in defaultState) {
      const storedRawValue = localStorage.getItem(key)
      if (storedRawValue === null) continue
      let processedValue = null
      try {
        processedValue = JSON.parse(storedRawValue as string)
      } catch {
        processedValue = storedRawValue
      }
      storageData[key as keyof T] = processedValue as T[keyof T]
    }
    setLocalState(storageData as T)
    setLoadingLS(false)

    const handleStorageChange = (e: StorageEvent) => { // This only fires in other tabs
      console.log('storage event', e)
      if (e.storageArea !== localStorage) return
      if (!(e.key && e.key in defaultState)) return
      const updatedData = {
        [e.key as keyof T]: e.newValue ? JSON.parse(e.newValue) : null,
      }
      setLocalState(prev => ({
        ...prev,
        ...updatedData,
      }))
    }
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }


  }, []);

  useEffect(() => {
    localStateRef.current = localState
  }, [localState]);

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        unsubRef.current = onSnapshot(doc(firestore, collection, user.uid), async (docSnap) => {
          const data = docSnap.data() as T
          if (data) {
            let patch: Partial<T> = {}
            if (prevFirestoreDataRef.current) { // only update changed fields else we trigger renders with every timeElapsed update
              for (const key in data) {
                const prevValue = prevFirestoreDataRef.current[key]
                const newValue = data[key]
                if (prevValue !== newValue) {
                  patch[key] = JSON.parse(newValue)
                }
              }
            }
            else {
              patch = deserialize(data) as Partial<T>
            }
            setRemoteState(state => ({...(state || defaultState), ...patch}))
            prevFirestoreDataRef.current = data
          }
          else {
            const ref = doc(firestore, collection, user.uid);
            await setDoc(ref, serialize(localStateRef.current));
            wipeLocalStorage()
          }
          setUserId(user.uid)
          setLoadingFS(false);
        })
      } else {
        setLoadingFS(false);
        setRemoteState(null)
        prevFirestoreDataRef.current = null
        if (unsubRef.current) {
          unsubRef.current();
          unsubRef.current = null;
          setRemoteState(null)
          setUserId(null)
        }
      }
    })
  }, []);

  const wipeLocalStorage = () => {
    for (const key in defaultState) {
      const value = localStorage.getItem(key)
      if (value !== null) {
        localStorage.removeItem(key)
        localStorage.setItem(`backup_${key}`, value)
      }
    }
  }

  const setState: Setter<T> = async (patch: Partial<T>) => {

    const serializedPatch = serialize(patch as Record<string, any>) as T

    // 2) Persist to localStorage
    if (typeof window !== "undefined" && !remoteState) {
      for (const key in patch) {
        try {
          localStorage.setItem(key, serializedPatch[key as keyof T]);
          setLocalState((prev) => ({ ...prev, ...patch }))
        }
        catch (e) {
          console.error("Error saving to localStorage:", e);
        }
      }
    }


    // 3) If authed, upsert/merge to Firestore
    if (userId) {
      const emergencyMessage = await gameStatEmergencyError(collection, userId, patch)
      if (emergencyMessage) {
        ToastService.create(emergencyMessage, 'Stats Save Aborted')
        return
      }
      const ref = doc(firestore, collection, userId);
      await setDoc(ref, serializedPatch, { merge: true });
    }
  };

  const loading = loadingLS || loadingFS
  const resolved = remoteState ?? localState ?? defaultState

  const value = useMemo<FLSContextType<T>>(
    () => ({ state: resolved, setState, loading }),
    [JSON.stringify(resolved), loading] // stable enough for primitive-like T; swap for deep-equal if needed
  );

  return <FLSContext.Provider value={value}>{children}</FLSContext.Provider>
}

export default FLSProvider

export function useFLSFullState<T extends Record<string, any>>() {
  const ctx = useContext(FLSContext) as FLSContextType<T> | null;
  if (!ctx) throw new Error("useFLSFullState must be used within <FLSProvider>");
  return ctx; // { state, setState, loading }
}

export function useFLS<
  T extends Record<string, any>,
  K extends keyof T
>(
  key: K,
  fallback?: T[K]
): [T[K], (val: T[K]) => void, boolean] {
  const ctx = useFLSFullState<T>();
  const value = (ctx.state?.[key] ?? fallback) as T[K];
  const setValue = (val: T[K]) =>
    ctx.setState(({ [key]: val } as unknown) as Partial<T>);
  return [value, setValue, ctx.loading];
}

type LoaderProps = {
  children: React.ReactNode
}
export const FLSLoader = ({children}: LoaderProps) => {
  const ctx = useFLSFullState<any>()
  if (ctx.loading) return <Dimmer loading/>
  return <>{children}</>
}